import { useEffect, useMemo, useReducer, useRef } from "react"
import * as THREE from "three"
import { useGLTF, MeshTransmissionMaterial, Environment } from "@react-three/drei"
import { easing } from 'maath'
import { BallCollider, Physics, RigidBody, useRapier } from '@react-three/rapier'
import { useFrame } from "@react-three/fiber"
import { InstancedRigidBodies } from '@react-three/rapier'
import { Instances, Instance } from '@react-three/drei'
import { useBreakpoints } from "../../../hook/useBreakpoints"
import { useDeviceTilt } from "../../../hook/useDeviceTilt"

//gyro 

export function GravityFromTilt({ strength = 10000, deadzone = 0.02, neutralPitchDeg = 45, }) {
    const { world } = useRapier();
    const getAngles = useDeviceTilt();


    useFrame(() => {
        const { pitch, roll } = getAngles(); // radians

        // Neutralité à 45° sur le pitch (avant/arrière)
        const neutral = THREE.MathUtils.degToRad(neutralPitchDeg);
        const pitchBiased = pitch - neutral;

        // Normalisation (±90° -> ±1)
        const norm = (a) => THREE.MathUtils.clamp(a / (Math.PI / 2), -1, 1);

        // Mapping correct :
        //  - roll (gamma) -> X (gauche/droite)
        //  - pitch biaisé -> Y (haut/bas)
        const sx = Math.abs(roll) < deadzone ? 0 : norm(roll);
        const sy = Math.abs(pitchBiased) < deadzone ? 0 : norm(pitchBiased);

        // Pencher en avant => descend (Y négatif)
        world.gravity = { x: sx * strength, y: -sy * strength, z: 0 };
    });

    return null;
}
//fingyro

const accents = ['#4060ff', '#FF5E5E']

export function useAccentReducer() {
    const impulseFns = useRef([])

    const [accent, dispatch] = useReducer(
        (state) => ++state % accents.length,
        0
    )

    const registerImpulse = (fn) => {
        impulseFns.current.push(fn)
    }

    const click = () => {
        dispatch()
        impulseFns.current.forEach((fn) => fn())
    }


    return { accents, accent, click, registerImpulse }
}

export default function HeaderEffect({ accents, accent, click, registerImpulse }) {

    const { isMobile } = useBreakpoints()


    const shuffle = (accent = 0) => [
        { color: '#444', roughness: 0.1, metalness: 0. },
        { color: '#444', roughness: 0.1, metalness: 0.2 },
        { color: '#444', roughness: 0.75, metalness: 0.2 },
        { color: '#444', roughness: 0.75, metalness: 0.2 },
        { color: '#444', roughness: 0.75, metalness: 0.8 },
        { color: 'white', roughness: 0.1, metalness: 0.2 },
        { color: 'white', roughness: 0.1, metalness: 0.2 },
        { color: 'white', roughness: 0.75, metalness: 0.2 },
        { color: 'white', roughness: 0.75, metalness: 0.2 },
        { color: 'white', roughness: 0.1, metalness: 0.2 },
        { color: accents[accent], roughness: 0.1, accent: true, metalness: 0.2 },
        { color: accents[accent], roughness: 0.75, accent: true, metalness: 0.2 },
        { color: accents[accent], roughness: 0.75, accent: true, metalness: 0.2 },
        { color: accents[accent], roughness: 0.1, accent: true, metalness: 0.2 },
        { color: accents[accent], roughness: 0.1, accent: true, metalness: 0.2 },
        // {color:'green', roughness:0, metalness: 0 }
    ]


    const connectors = useMemo(() => shuffle(accent), [accent])
    return (
        <>
            <color attach="background" args={['#482BFF']} />
            <Environment preset="studio" backgroundBlurriness={0.5} environmentIntensity={0.8} />
            <Physics
                // debug 
                gravity={[0, 0, 0]}>

                {isMobile ? <GravityFromTilt strength={100} deadzone={0} /> : null}

                <Pointer />
                {connectors.map((props, i) => <Connector key={i} {...props} onClickImpulse={registerImpulse} />)}
                <Connector position={[10, 10, 5]} onClickImpulse={registerImpulse}>

                    <Ovni>
                        <MeshTransmissionMaterial clearcoat={1} thickness={0.4} anisotropicBlur={0.4} chromaticAberration={0.5} samples={8} resolution={1024} />
                    </Ovni>
                </Connector>
            </Physics>

        </>
    )
}


function Connector({ position, children, vec = new THREE.Vector3(), scale, r = THREE.MathUtils.randFloatSpread, accent, onClickImpulse, ...props }) {
    const api = useRef()
    const pos = useMemo(() => position || [r(10), r(10), r(10)], [])
    useFrame((state, delta) => {
        delta = Math.min(0.1, delta)
        api.current?.applyImpulse(vec.copy(api.current.translation()).negate().multiplyScalar(0.8 * (delta / (1 / 60))))
    })

    useEffect(() => {
        if (onClickImpulse) {
            onClickImpulse(() => {
                if (api.current) {
                    // reset vitesse avant d'appliquer l'impulsion
                    api.current.setLinvel({ x: 0, y: 0, z: 0 }, true)

                    // calcul direction depuis le centre
                    const pos = api.current.translation()
                    const dir = new THREE.Vector3(pos.x, pos.y, pos.z)
                        .normalize()
                        .multiplyScalar(50) // force constante

                    api.current.applyImpulse(dir, true)
                }
            })
        }
    }, [onClickImpulse])

    return (
        <RigidBody linearDamping={4} angularDamping={1} friction={0.1} position={pos} ref={api} colliders={false}>
            <BallCollider args={[1.01]} />
            {children ? children : <Ovni {...props} />}
            {accent && <pointLight intensity={4} distance={2.5} color={props.color} />}
        </RigidBody>
    )

}

function Pointer({ vec = new THREE.Vector3() }) {
    const ref = useRef()
    useFrame(({ pointer, viewport }) => {
        ref.current?.setNextKinematicTranslation(vec.set((pointer.x * viewport.width) / 2, (pointer.y * viewport.height) / 2, 0))
    })
    return (
        <RigidBody position={[0, 0, 0]} type="kinematicPosition" colliders={false} ref={ref}>
            <BallCollider args={[1]} />
        </RigidBody>
    )
}


function Ovni({ children, color = "white", roughness = 0, metalness = 0.2 }, ...props) {
    const ovni = useRef()
    const { nodes, materials } = useGLTF('./models/ovni-header.glb')
    useFrame((state, delta) => {
        easing.dampC(ovni.current.material.color, color, 0.2, delta)
    })
    return (
        <mesh ref={ovni} castShadow receiveShadow scale={1.5} geometry={nodes.ovni.geometry} >
            <meshStandardMaterial metalness={metalness} roughness={roughness} map={materials.base.map} emissive="#482BFF" emissiveIntensity={0.05} />
            {children}
        </mesh>
    )
}